/* eslint-disable jira/integration/test-id-by-folder-structure */

/** @jsx jsx */

import React, { memo, useCallback, useState } from 'react';
import { jsx } from '@compiled/react';
import { DrawerContent } from '@atlaskit/drawer/drawer-content';
import { Drawer } from '@atlaskit/drawer';

import { Inline } from '@atlaskit/primitives/compiled/inline';
import Spinner from '@atlaskit/spinner/spinner';

import { ErrorFlag, FlagGroup } from '@atlassian/jira-flags';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import type { StageProps } from './StageProps.types.tsx';
import type { OutboundConnection } from './CommonApplicationLinkCreationEntity.tsx';
import CreateSubpage from './CreateSubpage.tsx';
import ReviewSubpage from './ReviewSubpage.tsx';
import ReciprocalPromptSubpage from './ReciprocalPromptSubpage.tsx';
import ConfirmationSubpage from './ConfirmationSubpage.tsx';
import ConnectSubpage from './ConnectSubpage.tsx';

const errorFlagTitleMessage = defineMessage({
	defaultMessage: 'Failed to configure application link',
	id: 'applinks.administration.ui.creation-flow-dialog.error-flag-title=',
	description: 'Title for the error flag displayed when application link configuration fails.',
});

const DEFAULT_CREATE_STAGE: StageProps = { kind: 'create' };

type StateMachineProps = {
	state: StageProps;
	onCancel: () => void;
	onStateChange: (newState: StageProps) => void;
	onDisplayError: (err: string) => void;
	availableConnections?: OutboundConnection[];
};

const StateMachine = memo((props: StateMachineProps) => {
	switch (props.state.kind) {
		case 'create':
		case 'manual-create':
			return (
				<CreateSubpage
					onError={props.onDisplayError}
					onSuccess={props.onStateChange}
					onCancel={props.onCancel}
				/>
			);
		case 'review':
			return (
				<ReviewSubpage
					state={props.state}
					onSuccess={props.onStateChange}
					onError={props.onDisplayError}
					onCancel={props.onCancel}
					availableConnections={props.availableConnections}
				/>
			);
		case 'reciprocal-prompt':
			return <ReciprocalPromptSubpage state={props.state} />;
		case 'connect':
			return (
				<ConnectSubpage
					state={props.state}
					onSuccess={props.onCancel} // Success = close the drawer
					onError={props.onDisplayError}
					onCancel={props.onCancel}
				/>
			);
		case 'confirmation':
			return <ConfirmationSubpage state={props.state} />;
		case 'loading':
			props.state.promise.then((newState) => {
				props.onStateChange(newState);
			});

			return (
				<Inline alignBlock="center" alignInline="center">
					<Spinner />
				</Inline>
			);
		default:
			return <></>;
	}
});

export type CreateFlowDialogProps = {
	isOpen: boolean;
	onExit: () => void;
	onCloseComplete?: () => void;
	startingStage?: StageProps;
	availableConnections?: OutboundConnection[];
};

export const CreateFlowDialog = (props: CreateFlowDialogProps): React.JSX.Element => {
	// Use the provided starting stage or default to the create stage
	const [flowState, setFlowState] = useState<StageProps>(
		props.startingStage || DEFAULT_CREATE_STAGE,
	);

	const { formatMessage } = useIntl();

	const onCancel = () => {
		props.onExit();
		const defaultStage = props.startingStage || DEFAULT_CREATE_STAGE;
		setFlowState(defaultStage);
	};

	const [errorFlags, setErrorFlags] = useState<Array<string>>([]);

	const addFlag = useCallback(
		(err: string) => {
			const newFlags = errorFlags.slice();

			newFlags.splice(0, 0, err);

			setErrorFlags(newFlags);
		},

		[errorFlags],
	);

	const handleDismiss = () => {
		setErrorFlags(errorFlags.slice(1));
	};

	useIntl();

	return (
		<Drawer
			testId="applinks-administration.ui.list.creation-flow-dialog.drawer"
			width="full"
			onClose={() => onCancel()}
			onCloseComplete={props.onCloseComplete}
			isOpen={props.isOpen}
		>
			<DrawerContent>
				<StateMachine
					state={flowState}
					onStateChange={setFlowState}
					onCancel={onCancel}
					onDisplayError={addFlag}
					availableConnections={props.availableConnections}
				/>
			</DrawerContent>
			<FlagGroup onDismissed={handleDismiss}>
				{errorFlags.map((err, idx) => {
					return (
						<ErrorFlag
							id={`flag-${idx}`}
							key={`flag-${idx}`}
							title={formatMessage(errorFlagTitleMessage)}
							description={err}
						/>
					);
				})}
			</FlagGroup>
		</Drawer>
	);
};
